import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { uploadImageToStorage, resizeImageForUpload } from '../../utils/imageUpload';

interface Popup {
  id: string;
  title: string;
  image_url: string;
  link_url: string | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

const PopupManagement: React.FC = () => {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPopup, setEditingPopup] = useState<Popup | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 폼 상태
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    start_date: '',
    end_date: '',
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    fetchPopups();
  }, []);

  const fetchPopups = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('popups')
      .select('*')
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching popups:', error);
    } else {
      setPopups(data || []);
    }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingPopup(null);
    const now = new Date();
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    setFormData({
      title: '',
      image_url: '',
      link_url: '',
      start_date: formatDateTimeLocal(now),
      end_date: formatDateTimeLocal(nextWeek),
      is_active: true,
      display_order: 0
    });
    setShowModal(true);
  };

  const openEditModal = (popup: Popup) => {
    setEditingPopup(popup);
    setFormData({
      title: popup.title,
      image_url: popup.image_url,
      link_url: popup.link_url || '',
      start_date: formatDateTimeLocal(new Date(popup.start_date)),
      end_date: formatDateTimeLocal(new Date(popup.end_date)),
      is_active: popup.is_active,
      display_order: popup.display_order
    });
    setShowModal(true);
  };

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // 메인 팝업은 여러 장이 동시에 뜨므로 업로드 전에 줄인다
      const resized = await resizeImageForUpload(file);
      const imageUrl = await uploadImageToStorage(resized, 'popups');
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('이미지 업로드에 실패했습니다.');
    }
    setUploading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (!formData.image_url) {
      alert('팝업 이미지를 업로드해주세요.');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      alert('노출 기간을 설정해주세요.');
      return;
    }

    setSaving(true);

    const popupData = {
      title: formData.title.trim(),
      image_url: formData.image_url,
      link_url: formData.link_url.trim() || null,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: new Date(formData.end_date).toISOString(),
      is_active: formData.is_active,
      display_order: formData.display_order
    };

    let error;

    if (editingPopup) {
      const result = await supabase
        .from('popups')
        .update(popupData)
        .eq('id', editingPopup.id);
      error = result.error;
    } else {
      const result = await supabase
        .from('popups')
        .insert(popupData);
      error = result.error;
    }

    if (error) {
      console.error('Error saving popup:', error);
      alert('저장에 실패했습니다.');
    } else {
      setShowModal(false);
      fetchPopups();
    }

    setSaving(false);
  };

  const handleDelete = async (popup: Popup) => {
    if (!window.confirm(`"${popup.title}" 팝업을 삭제하시겠습니까?`)) {
      return;
    }

    const { error } = await supabase
      .from('popups')
      .delete()
      .eq('id', popup.id);

    if (error) {
      console.error('Error deleting popup:', error);
      alert('삭제에 실패했습니다.');
    } else {
      fetchPopups();
    }
  };

  const toggleActive = async (popup: Popup) => {
    const { error } = await supabase
      .from('popups')
      .update({ is_active: !popup.is_active })
      .eq('id', popup.id);

    if (error) {
      console.error('Error toggling popup:', error);
    } else {
      fetchPopups();
    }
  };

  const getPopupStatus = (popup: Popup): { label: string; color: string } => {
    if (!popup.is_active) {
      return { label: '비활성', color: 'bg-gray-100 text-gray-600' };
    }

    const now = new Date();
    const start = new Date(popup.start_date);
    const end = new Date(popup.end_date);

    if (now < start) {
      return { label: '예정', color: 'bg-blue-100 text-blue-700' };
    }
    if (now > end) {
      return { label: '종료', color: 'bg-gray-100 text-gray-600' };
    }
    return { label: '노출중', color: 'bg-green-100 text-green-700' };
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}.${month}.${day} ${hours}:${minutes}`;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">팝업 관리</h1>
          <p className="text-sm text-gray-500 mt-1">홈페이지 메인 팝업을 관리합니다.</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-[#48c9a6] text-white rounded-lg hover:bg-[#3ab89a] transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          팝업 등록
        </button>
      </div>

      {/* Popup List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">로딩 중...</div>
      ) : popups.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          등록된 팝업이 없습니다.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">이미지</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">제목</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">노출 기간</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">활성화</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">관리</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {popups.map((popup) => {
                const status = getPopupStatus(popup);
                return (
                  <tr key={popup.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <img
                        src={popup.image_url}
                        alt={popup.title}
                        className="w-24 h-16 object-cover rounded border"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{popup.title}</div>
                      {popup.link_url && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {popup.link_url}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div>{formatDate(popup.start_date)}</div>
                      <div className="text-gray-400">~</div>
                      <div>{formatDate(popup.end_date)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleActive(popup)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          popup.is_active ? 'bg-[#48c9a6]' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            popup.is_active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditModal(popup)}
                          className="p-2 text-gray-600 hover:text-[#48c9a6] hover:bg-gray-100 rounded"
                          title="수정"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(popup)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded"
                          title="삭제"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingPopup ? '팝업 수정' : '팝업 등록'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* 제목 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제목 (관리용) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="예: 2025 겨울방학캠프 모집"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                />
              </div>

              {/* 이미지 업로드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  팝업 이미지 <span className="text-red-500">*</span>
                </label>
                {formData.image_url ? (
                  <div className="relative">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-48 object-contain border rounded-lg bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, image_url: '' }));
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#48c9a6] transition-colors"
                  >
                    {uploading ? (
                      <div className="text-gray-500">업로드 중...</div>
                    ) : (
                      <>
                        <PhotoIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">클릭하여 이미지 업로드</p>
                        <p className="text-xs text-gray-400 mt-1">권장: 가로 500px 이상</p>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* 링크 URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  링크 URL (선택)
                </label>
                <input
                  type="text"
                  value={formData.link_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                  placeholder="예: /programs/xxx 또는 https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">이미지 클릭 시 이동할 페이지 주소</p>
              </div>

              {/* 노출 기간 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시작일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    종료일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                  />
                </div>
              </div>

              {/* 표시 순서 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  표시 순서
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#48c9a6] focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">숫자가 작을수록 먼저 표시 (여러 팝업 동시 노출 시)</p>
              </div>

              {/* 활성화 */}
              <div className="flex items-center gap-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
                  />
                  <span className="ml-2 text-sm text-gray-700">활성화</span>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-[#48c9a6] text-white rounded-lg hover:bg-[#3ab89a] disabled:opacity-50"
                >
                  {saving ? '저장 중...' : (editingPopup ? '수정' : '등록')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupManagement;
